import api from '@/services/api';
import type { CampaignConfigSetting } from '../types/campaign.types';
import type { ConfigOptionRecord } from './smr-admin';
import type { CancellationRulesFormValues } from '../schemas/cancellationRules.schema';
import { BALANCE_ADJUSTMENT_FALLBACK } from '../schemas/cancellationRules.schema';

const smrPath = '/smr';

/** The master gate. Rendered separately and EXCLUDED from the five-field list. */
export const CANCELLATIONS_ALLOWED_KEY = 'cancellations_allowed';

/** ORDERING HINT ONLY, never a whitelist: an unlisted catalogue key still renders,
 * appended after these in catalogue (alphabetical) order. */
export const CANCELLATION_FIELD_ORDER = [
    'no_customer_refund',
    'suspension_period_months',
    'on_hold_period_months',
    'cooling_off_period_days',
    'positive_balance_adjustment_type',
    'negative_balance_adjustment_type',
] as const;

/** Derived, read-only display keys — never seeded into the form, validated or written. */
export const DERIVED_ADJUSTMENT_KEYS = [
    'positive_balance_adjustment_type',
    'negative_balance_adjustment_type',
] as const;

export type CancellationControl = 'checkbox' | 'number' | 'select';

export interface CancellationFieldMeta {
    key: string;
    label: string;
    control: CancellationControl;
    min?: number; // number control only
    max?: number; // number control only
    options?: string[]; // select control only
    configOptionId?: string;
}

export interface CancellationFieldMetasPartition {
    formFieldMetas: CancellationFieldMeta[];
    derivedFieldMetas: CancellationFieldMeta[];
}

/** Pick a control per data_type + validation_rules; derive bounds/enum. Excludes the master, and
 * partitions the derived keys out so neither reaches the controls, schema, seed or save payload. */
export const deriveFieldMetas = (options: ConfigOptionRecord[]): CancellationFieldMetasPartition => {
    const rank = (key: string) => {
        const i = (CANCELLATION_FIELD_ORDER as readonly string[]).indexOf(key);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    // Copy before sorting: must never mutate the React Query `catalogQuery.data.data`
    // array in place, even though `.filter()` returns a fresh array today.
    const metas = [...options]
        .filter((o) => o.key_name !== CANCELLATIONS_ALLOWED_KEY)
        .sort((a, b) => rank(a.key_name) - rank(b.key_name) || a.key_name.localeCompare(b.key_name))
        .map((o) => {
            const vr = (o.validation_rules ?? {}) as { min?: number; max?: number; in?: string[] };
            if (o.data_type === 'integer') {
                return {
                    key: o.key_name,
                    label: o.label ?? o.key_name,
                    control: 'number' as const,
                    min: vr.min,
                    max: vr.max,
                    configOptionId: o.id,
                };
            }
            if (o.data_type === 'string') {
                return {
                    key: o.key_name,
                    label: o.label ?? o.key_name,
                    control: 'select' as const,
                    options: vr.in ?? [...BALANCE_ADJUSTMENT_FALLBACK],
                    configOptionId: o.id,
                };
            }
            // 'boolean' (and any unknown type) -> checkbox
            return {
                key: o.key_name,
                label: o.label ?? o.key_name,
                control: 'checkbox' as const,
                configOptionId: o.id,
            };
        });

    const isDerived = (key: string) => (DERIVED_ADJUSTMENT_KEYS as readonly string[]).includes(key);
    return {
        formFieldMetas: metas.filter((m) => !isDerived(m.key)),
        derivedFieldMetas: metas.filter((m) => isDerived(m.key)),
    };
};

/** Defensive boolean coercion — Boolean("false") === true. Backend value may be
 * 1/0/"1"/"0"/true/false rather than a JSON boolean. */
export const toBool = (value: unknown): boolean =>
    value === true || value === 1 || value === '1' || value === 'true';

/** Seed the form from formFieldMetas only — neither derived key can reach values.fields. */
export const settingsToCancellationForm = (
    settings: CampaignConfigSetting[],
    formFieldMetas: CancellationFieldMeta[],
): CancellationRulesFormValues => {
    const byKey = new Map(settings.map((s) => [s.key, s.value]));
    const fields: Record<string, string | boolean> = {};
    for (const meta of formFieldMetas) {
        const stored = byKey.get(meta.key);
        if (meta.control === 'checkbox') fields[meta.key] = toBool(stored);
        else if (stored === undefined || stored === null) fields[meta.key] = '';
        else fields[meta.key] = String(stored); // number held as raw string; enum as value string
    }
    return { cancellations_allowed: toBool(byKey.get(CANCELLATIONS_ALLOWED_KEY)), fields };
};

export interface CancellationSettingRow {
    key: string;
    value: boolean | number | string;
    config_option_id?: string;
}

/** Always writes cancellations_allowed; field rows only when enabled. An empty number
 * or enum is omitted so it neither 422s the backend rule nor clobbers a stored value. */
export const cancellationFormToSettings = (
    values: CancellationRulesFormValues,
    formFieldMetas: CancellationFieldMeta[],
): CancellationSettingRow[] => {
    const rows: CancellationSettingRow[] = [
        {
            key: CANCELLATIONS_ALLOWED_KEY,
            value: values.cancellations_allowed,
            config_option_id: formFieldMetas.find((m) => m.key === CANCELLATIONS_ALLOWED_KEY)?.configOptionId,
        },
    ];
    if (!values.cancellations_allowed) return rows;

    // The caller may pass formFieldMetas plus a synthetic master entry (for the .find() above);
    // skip it here so it is not read a second time out of values.fields and pushed as `false`.
    for (const meta of formFieldMetas) {
        if (meta.key === CANCELLATIONS_ALLOWED_KEY) continue;
        if ((DERIVED_ADJUSTMENT_KEYS as readonly string[]).includes(meta.key)) continue;
        const raw = values.fields[meta.key];
        if (meta.control === 'checkbox') {
            rows.push({ key: meta.key, value: Boolean(raw), config_option_id: meta.configOptionId });
        } else if (meta.control === 'number') {
            if (raw === '' || raw === undefined) continue; // omit empty (D-int)
            rows.push({ key: meta.key, value: Number(raw), config_option_id: meta.configOptionId });
        } else {
            if (raw === '' || raw === undefined) continue; // omit unselected enum
            rows.push({ key: meta.key, value: String(raw), config_option_id: meta.configOptionId });
        }
    }
    return rows;
};

/** Single POST attach — idempotent upsert-by-key. Mirrors fetchCampaignConfigSettings unwrap. */
export const saveCancellationConfigSettings = async (
    campaignId: string,
    rows: CancellationSettingRow[],
): Promise<CampaignConfigSetting[]> => {
    const response = await api.post(`${smrPath}/campaigns/${campaignId}/config-settings`, { settings: rows });
    return (response.data.data ?? response.data) as CampaignConfigSetting[];
};
